import { useEffect } from "react";
import { useThree } from "@react-three/fiber";

/**
 * Neutralise the giant frame-delta R3F produces the first frame after a
 * backgrounded tab regains focus.
 *
 * Browsers suspend requestAnimationFrame while a tab is hidden, so when the
 * player clicks away and comes back the render clock has a huge accumulated
 * delta. R3F passes that delta straight to every `useFrame` consumer — rapier
 * steps the physics world by it and ecctrl's float-spring applies a
 * delta-scaled impulse — which launches the player capsule through the floor
 * into the void (reported as "falling off the map into a dark screen, have to
 * refresh"). Consuming the delta on the visibility/focus transition makes the
 * next resumed frame see a normal-sized step, so nothing integrates the gap.
 *
 * Mount this anywhere inside a <Canvas>.
 */
export function TabRefocusGuard(): null {
  const clock = useThree((s) => s.clock);
  useEffect(() => {
    const flush = (): void => {
      if (document.visibilityState !== "visible") return;
      // R3F's render loop reads clock.getDelta() once per frame; consuming the
      // accumulated value here resets oldTime so the next frame's dt is ~0.
      clock.getDelta();
    };
    document.addEventListener("visibilitychange", flush);
    window.addEventListener("focus", flush);
    return () => {
      document.removeEventListener("visibilitychange", flush);
      window.removeEventListener("focus", flush);
    };
  }, [clock]);
  return null;
}

export interface WebGLContextRecoveryProps {
  /** Context lost — caller should pause the render loop + unmount post-FX. */
  onLost: () => void;
  /** Context restored — caller should resume + remount post-FX. */
  onRestored: () => void;
  /**
   * The tab regained focus but the context is still lost (the browser never
   * fired `restored` — common after a long AFK / tab discard). Caller should
   * force a full Canvas remount to get a fresh context.
   */
  onUnrecovered: () => void;
}

// How long to let the browser restore the context on its own before we give up
// and force a fresh Canvas. Keep short — a black screen for >2s reads as broken.
const RESTORE_GRACE_MS = 1500;
// Watchdog cadence — catches losses that NEVER fire `webglcontextlost` (a hard
// GPU-process drop) by polling `isContextLost()` directly.
const WATCHDOG_MS = 2000;

/**
 * Recover from WebGL context loss instead of dying on a permanent black screen.
 *
 * Browsers reclaim the GPU context aggressively to save power — when a tab is
 * backgrounded AND when a foreground tab just sits idle (no draws to keep the
 * GPU "hot", especially on dual-GPU laptops where the OS switches back to the
 * integrated GPU). Both end in the same black screen.
 *
 * Recovery, in order:
 *  1. On `webglcontextlost` we `preventDefault()` (ask the browser to restore)
 *     and tell the caller to PAUSE the loop + unmount post-FX — otherwise the
 *     EffectComposer renders against a freed target and throws
 *     `reading 'alpha'`, which the ErrorBoundary turns into the black screen.
 *  2. If `webglcontextrestored` fires within a short grace, we resume in place
 *     (seamless, no reload).
 *  3. If it does NOT (the browser often won't), a grace TIMER force-remounts the
 *     whole Canvas for a fresh context. This is timer-driven, NOT gated on a
 *     focus/visibility event — so it recovers the "sat still and went black"
 *     case where no such event ever fires. (We defer the remount while hidden,
 *     since creating a GL context in a background tab is unreliable, and retry.)
 *  4. A watchdog polls `isContextLost()` to catch losses that never fired the
 *     event at all.
 *
 * Mount inside <Canvas>.
 */
export function WebGLContextRecovery({
  onLost,
  onRestored,
  onUnrecovered,
}: WebGLContextRecoveryProps): null {
  const gl = useThree((s) => s.gl);
  const invalidate = useThree((s) => s.invalidate);
  useEffect(() => {
    const canvas = gl.domElement;
    let lost = false;
    let grace: ReturnType<typeof setTimeout> | null = null;

    const clearGrace = (): void => {
      if (grace) {
        clearTimeout(grace);
        grace = null;
      }
    };

    // Force a fresh Canvas if the context is still lost. Deferred while the tab
    // is hidden (a new GL context in a background tab is unreliable) — the
    // watchdog / visibility handler retries once we're visible again.
    const forceRemountIfStillLost = (): void => {
      if (!lost) return;
      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        return;
      }
      const ctx = gl.getContext();
      if (lost || (ctx != null && ctx.isContextLost())) {
        // eslint-disable-next-line no-console
        console.warn("[webgl] context not restored — forcing canvas remount");
        onUnrecovered();
      }
    };

    const enterLost = (): void => {
      if (lost) return;
      lost = true;
      // eslint-disable-next-line no-console
      console.warn("[webgl] context lost — pausing render");
      onLost();
      clearGrace();
      grace = setTimeout(forceRemountIfStillLost, RESTORE_GRACE_MS);
    };

    const handleLost = (e: Event): void => {
      e.preventDefault();
      enterLost();
    };
    const handleRestored = (): void => {
      lost = false;
      clearGrace();
      // eslint-disable-next-line no-console
      console.warn("[webgl] context restored — resuming");
      onRestored();
      invalidate();
    };
    // Retry the remount as soon as we become visible (covers a loss that
    // happened while hidden, where the grace timer bailed out).
    const handleVisible = (): void => {
      if (document.visibilityState === "visible") forceRemountIfStillLost();
    };
    // Catch a hard GPU drop that never fired `webglcontextlost`.
    const watchdog = setInterval(() => {
      const ctx = gl.getContext();
      if (ctx != null && ctx.isContextLost()) {
        if (!lost) enterLost();
        else forceRemountIfStillLost();
      }
    }, WATCHDOG_MS);

    canvas.addEventListener("webglcontextlost", handleLost, false);
    canvas.addEventListener("webglcontextrestored", handleRestored, false);
    document.addEventListener("visibilitychange", handleVisible);
    window.addEventListener("focus", handleVisible);
    return () => {
      canvas.removeEventListener("webglcontextlost", handleLost);
      canvas.removeEventListener("webglcontextrestored", handleRestored);
      document.removeEventListener("visibilitychange", handleVisible);
      window.removeEventListener("focus", handleVisible);
      clearInterval(watchdog);
      clearGrace();
    };
  }, [gl, invalidate, onLost, onRestored, onUnrecovered]);
  return null;
}
